import React, { useEffect, useState } from 'react';
import axios from 'axios';

const GROUPS = {
  "Dashboard": ["dashboard.view"],
  "Documents": ["documents.read", "documents.write"],
  "Events": ["events.create", "events.manage"],
  "Voting & Minutes": ["voting.view", "voting.manage", "minutes.sign", "minutes.publish"],
  "Newsletter": ["newsletter.manage"],
  "Payments": ["payments.view", "payments.manage"],
  "Violations": ["violations.view", "violations.manage"],
  "Smart Pro": ["smartpro.view", "smartpro.admin"],
  "Settings": ["settings.manage"],
};

const PERM_LABELS = {
  "dashboard.view": "View Dashboard",
  "documents.read": "Read Documents",
  "documents.write": "Upload/Edit Documents",
  "events.create": "Create Events",
  "events.manage": "Manage All Events",
  "voting.view": "View Voting",
  "voting.manage": "Manage Voting",
  "minutes.sign": "Sign Minutes",
  "minutes.publish": "Publish Minutes",
  "newsletter.manage": "Manage Newsletter",
  "payments.view": "View Payments",
  "payments.manage": "Manage Payments",
  "violations.view": "View Violations",
  "violations.manage": "Manage Violations",
  "smartpro.view": "View Smart Pro",
  "smartpro.admin": "Admin Smart Pro",
  "settings.manage": "Manage Settings",
};

export default function TeamPermissions({ hoaId, api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPermissions();
  }, [hoaId]);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/hoa/${hoaId}/settings/permissions`);
      if (response.data.success) {
        setRows(response.data.perms || []);
      }
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError(err.response?.data?.detail || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (userId, perm) => {
    setRows(prev => prev.map(r => 
      r.user_id === userId
        ? {
            ...r, 
            perms: r.perms.includes(perm) 
              ? r.perms.filter(p => p !== perm) 
              : [...r.perms, perm]
          }
        : r
    ));
  };

  const saveUser = async (userId) => {
    try {
      setSaving(userId);
      const row = rows.find(r => r.user_id === userId);
      await api.put(`/api/hoa/${hoaId}/settings/permissions/${userId}`, {
        perms: row.perms
      });
      alert('✅ Permissions saved successfully!');
    } catch (err) {
      console.error('Error saving permissions:', err);
      alert('❌ Failed to save permissions: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button
          onClick={loadPermissions}
          className="mt-2 text-red-600 hover:text-red-800 font-medium"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-yellow-900 mb-2">No Team Members Found</h3>
        <p className="text-yellow-800">
          Add team members first, then their permissions will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="text-sm font-medium text-blue-900 mb-1">🔐 Granular Permission Control</h3>
        <p className="text-sm text-blue-800">
          Check or uncheck permissions for each team member. Click "Save" after making changes.
          These permissions control actual access to features throughout the application.
        </p>
      </div>

      {rows.map(r => {
        const fullName = `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.email;
        
        return (
          <div key={r.user_id} className="rounded-xl border border-gray-200 bg-white shadow-sm p-6">
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
              <div>
                <div className="font-semibold text-lg text-gray-900">{fullName}</div>
                <div className="text-sm text-gray-600">{r.email}</div>
                <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded">
                  {r.role?.replace(/_/g, ' ').toUpperCase() || 'TEAM MEMBER'}
                </span>
              </div>
              <div className="text-sm text-gray-500">
                {r.perms?.length || 0} permissions
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {Object.entries(GROUPS).map(([label, permList]) => (
                <div key={label} className="space-y-2">
                  <div className="text-sm font-bold text-gray-700 mb-3 pb-2 border-b border-gray-200">
                    {label}
                  </div>
                  {permList.map(p => (
                    <label key={p} className="flex items-start gap-2 text-sm hover:bg-gray-50 p-1 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={r.perms?.includes(p) || false}
                        onChange={() => toggle(r.user_id, p)}
                        className="mt-0.5 text-indigo-600 focus:ring-indigo-500 rounded"
                      />
                      <span className="text-gray-700">{PERM_LABELS[p] || p}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => saveUser(r.user_id)}
                disabled={saving === r.user_id}
                className={`px-4 py-2 rounded-md font-medium text-white ${
                  saving === r.user_id
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {saving === r.user_id ? 'Saving...' : '💾 Save Permissions'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
